import * as React from 'react';

import { autorun, IReactionDisposer } from 'mobx';
import { observer } from 'mobx-react';

import { Button, TextButton } from '@estee/elc-buttons';

import { AdditionalTerms } from '~components/AdditionalTerms';
import { Alert, AlertActionContainer, AlertTitle } from '~components/Alert';
import { goBindAccountWithLine, ROOT_NODE_CLASS } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { SignUpType } from '~constants/SignUpTypeEnum';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { classes } from '~utils/Classes';
import { useTranslation } from '~utils/hooks/translations';
import { scrollTo } from '~utils/ScrollToUtils';
import { ILinkLabel, isLinkValid } from '~utils/TranslationLabelUtils';
import { DynamicForm } from '~views/common/DynamicForm';
import { MessagePanel } from '~views/common/MessagePanel';
import { Otp } from '~views/otp/Otp';
import { SocialButton } from '~views/social-sign-in/SocialButton';
import { SocialPositionWrapper } from '~views/social-sign-in/SocialPositionWrapper';

import { SignUpViewController } from './SignUpViewController';

export interface ISignUpProps {
    /** Overrides default submit button label */
    submitButtonLabel?: string;
    signUpType?: SignUpType;
    hideSocialButtons?: boolean;
}

const CREATE_NEW_RECORD_TEXT = 'elc_account.create_new_record_text';

const submitButtonClassNames = classes('iam-submit-button');
const submitButtonWrapperClassNames = classes('iam-submit-button-wrapper');

export const SignUp = observer(function _SignUp(props: ISignUpProps) {
    let disposer: IReactionDisposer;
    const viewController: SignUpViewController = diContainer.get(serviceNames.signUpViewController);
    const translations = useTranslation();
    const {
        submitLabel,
        loadingLabel,
        signInLabel,
        alreadyRegisteredMessage,
        reachBALabel
    } = translations;

    const {
        data: {
            triageAlertState: { visible: triageAlertVisible, shouldShowSignInButton },
            additionalTermsLabelKey,
            configNodeName,
            errorMessages,
            fieldsValidations,
            formSchema,
            formStore,
            hideSocialButtons: normalHideSocialButtons,
            showOtp,
            socialConfig
        },
        signInClicked,
        submitButtonClicked
    } = viewController;

    const {
        submitButtonLabel,
        signUpType = SignUpType.PARTIAL,
        // for normal workflow, we calculate whether to show social button providers and for consent form, we don't show social button providers
        hideSocialButtons = normalHideSocialButtons
    } = props;

    const rampSignUpClassNames = classes('iam-signup', `elc-iam-${configNodeName}-content`);

    React.useEffect(() => {
        const { scrollComplete } = viewController;

        const loaded = async () => {
            await viewController.loaded();
        };

        loaded();

        const scrollToElement = (element: HTMLElement) => {
            scrollTo(element, element);
            scrollComplete();
        };

        disposer = autorun(() => {
            const {
                data: { validationObject },
                shouldScroll
            } = viewController;

            if (!shouldScroll) {
                return;
            }

            if (Object.entries(validationObject).length !== 0) {
                const firstErrorDom = document.querySelector<HTMLElement>(
                    `input[name=${Object.keys(validationObject)[0]}]`
                );
                if (firstErrorDom) {
                    // Get the grandparent wrapper if it's DropDown component
                    const firstErrorDomElement =
                        firstErrorDom.parentElement?.parentElement || firstErrorDom;
                    scrollToElement(firstErrorDomElement);
                }
            } else {
                const rootNode = document.querySelector<HTMLElement>(ROOT_NODE_CLASS);
                if (rootNode) {
                    scrollToElement(rootNode);
                }
            }
        });

        return () => {
            return disposer?.();
        };
    }, []);

    const submitForm = React.useCallback(() => submitButtonClicked(signUpType), [signUpType]);

    // TODO: APACRIGEL-2052 why only show OTP if signUpType !== SignUpType.COMPLETE?
    // for social-sign-up flow, the `SignUpType` is COMPLETE but we need OTP
    // TODO: APACRIGEL-2052 there's also similar logic in AccountAccessPage, and SignUp is a child component of it, shall we consider making OTP as a new route?
    // TODO: APACRIGEL-2052 all logics should behind "showOtp" - "shoOtp" decides all if should show OTP
    if (showOtp) {
        return <Otp />;
    }

    const { isSubmitting } = formStore;
    const bindWithLine = translations[goBindAccountWithLine] as ILinkLabel;

    return (
        <div className={rampSignUpClassNames}>
            {triageAlertVisible && (
                <Alert>
                    <AlertTitle>{alreadyRegisteredMessage}</AlertTitle>
                    <p>{translations[CREATE_NEW_RECORD_TEXT]}</p>
                    <p>{reachBALabel}</p>
                    <AlertActionContainer className="elc-iam-triage-alert-cta-container">
                        {isLinkValid(bindWithLine) && (
                            <SocialButton
                                provider={SignInType.LINE}
                                onClick={() => window.open(bindWithLine.path, '_blank')}
                                buttonStyle="rect"
                            >
                                {bindWithLine.title}
                            </SocialButton>
                        )}
                        {shouldShowSignInButton && (
                            <TextButton onClick={signInClicked} title={signInLabel}>
                                {signInLabel}
                            </TextButton>
                        )}
                    </AlertActionContainer>
                </Alert>
            )}
            <MessagePanel messages={errorMessages} />
            <SocialPositionWrapper
                socialWrapperConfig={socialConfig}
                hideSocialButtons={hideSocialButtons}
            >
                <>
                    <DynamicForm
                        formSchema={formSchema}
                        formStore={formStore}
                        validations={fieldsValidations}
                        name="userForm"
                        submit={submitForm}
                    />
                    <div className={submitButtonWrapperClassNames}>
                        <Button
                            type="submit"
                            className={submitButtonClassNames}
                            disabled={isSubmitting}
                            onClick={submitForm}
                        >
                            {isSubmitting ? loadingLabel : submitButtonLabel ?? submitLabel}
                        </Button>
                    </div>
                    <AdditionalTerms additionalTermsLabelKey={additionalTermsLabelKey} />
                </>
            </SocialPositionWrapper>
        </div>
    );
});
